// Tablero de Preventa: una columna por estado del flujo, carriles opcionales (dueño o prioridad), filtros
// rápidos y arrastrar y soltar nativo. Un caso solo se suelta en un estado que la API devuelve en
// `acciones_permitidas` (cambiar_estado:<ESTADO>); el orden dentro de la columna se guarda en `orden_tablero`.
import { hrefRuta } from '../lib/rutas';
import { useCallback, useMemo, useRef, useState, type DragEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/cliente';
import type { CasoDetalle, CasoResumen, EstadoCaso, Prioridad } from '../api/tipos';
import { useSesion } from '../auth/contexto';
import { BarraFiltros } from '../components/BarraFiltros';
import { ChipEstado, IconoPrioridad, IconoTipo, PildoraSalud } from '../components/Chips';
import { Esqueleto, PanelError } from '../components/Estados';
import { Icono } from '../components/Iconos';
import { Avatar, ItemMenu, Pestanas, Popover, SeparadorMenu } from '../components/Ui';
import { useCambiarCaso, useNombres, useTodosLosCasos } from '../hooks/useCasos';
import { leer, guardar } from '../lib/almacen';
import { claseEstado, ESTADOS_CERRADOS, ETIQUETA_ESTADO, ETIQUETA_PRIORIDAD, grupoDeEstado } from '../lib/etiquetas';
import { aplicarFiltros, estaVencido, filtrosAUrl, filtrosDesdeUrl, SIN_DUENO, type FiltrosRapidos } from '../lib/filtros';
import { dinero, dineroCorto, fecha, pct } from '../lib/formato';
import { ACCION, destinosManuales, esPreventa, puedeOperar, tieneAccion, veFinanciero } from '../lib/permisos';
import { useUi } from '../shell/contexto';

interface Columna {
  clave: string;
  titulo: string;
  estados: EstadoCaso[];
  /** Estado al que pasa un caso soltado aquí (las columnas compuestas no reciben casos). */
  destino: EstadoCaso | null;
}

type VistaPipeline = 'activos' | 'licitaciones' | 'seguimiento' | 'cerrados';

const col = (e: EstadoCaso, destino = true): Columna => ({ clave: e, titulo: ETIQUETA_ESTADO[e], estados: [e], destino: destino ? e : null });
const TRABAJO: EstadoCaso[] = ['RECIBIDO', 'EN_ANALISIS', 'ESPERANDO_CLIENTE', 'COSTEO_RIESGO', 'ESPERANDO_COSTEO', 'CASO_NEGOCIO', 'PROPUESTA_ASEGURAMIENTO', 'POR_APROBAR'];
const VISTAS: { clave: VistaPipeline; titulo: string; columnas: Columna[]; filtro: (c: CasoResumen) => boolean }[] = [
  { clave: 'activos', titulo: 'Activos', columnas: [...TRABAJO.map((e) => col(e)), col('EXCEPCION', false)], filtro: () => true },
  { clave: 'licitaciones', titulo: 'Licitaciones', columnas: [...TRABAJO.map((e) => col(e)), col('APROBADO'), col('ENVIADO'), col('SEGUIMIENTO')], filtro: (c) => c.tipo === 'licitacion' },
  { clave: 'seguimiento', titulo: 'Seguimiento', columnas: [col('APROBADO'), col('ENVIADO'), col('SEGUIMIENTO')], filtro: () => true },
  { clave: 'cerrados', titulo: 'Cerrados', columnas: ESTADOS_CERRADOS.map((e) => col(e, false)), filtro: () => true },
];

const COLOR_GRUPO: Record<string, string> = {
  entrada: '#8b95a7',
  trabajo: '#2e7cf6',
  decision: '#7c3aed',
  seguimiento: '#0e8aa6',
  cerrados: '#16a34a',
  excepcion: '#dc2626',
};

type Carriles = 'ninguno' | 'dueno' | 'prioridad';

const ordenEfectivo = (c: CasoResumen, i: number) => (typeof c.orden_tablero === 'number' ? c.orden_tablero : 4e9 + i * 1000);
const ordenar = (a: CasoResumen, b: CasoResumen) =>
  (a.orden_tablero ?? Number.MAX_SAFE_INTEGER) - (b.orden_tablero ?? Number.MAX_SAFE_INTEGER) || b.case_id.localeCompare(a.case_id);


/** Explica por qué un caso no puede ir a una columna. */
function razonBloqueo(d: CasoDetalle, col: Columna): string | null {
  const desde = ETIQUETA_ESTADO[d.caso.estado];
  if (col.estados.includes(d.caso.estado)) return null;
  if (!tieneAccion(d.acciones_permitidas, ACCION.editar)) {
    return ESTADOS_CERRADOS.includes(d.caso.estado) ? 'El caso está cerrado y no admite cambios de estado.' : 'Su rol no puede mover casos en el tablero.';
  }
  if (ESTADOS_CERRADOS.includes(col.estados[0])) return 'Los cierres (ganado, perdido, desistido o sin oferta) se registran en la pestaña Cierre del caso.';
  if (col.clave === 'EXCEPCION') return 'Las excepciones las abre el sistema cuando un caso se detiene; no se mueve un caso a Excepción a mano.';
  if (col.destino === 'APROBADO' && d.caso.estado === 'POR_APROBAR') return 'La aprobación se registra en Ofertas y aprobación, con el nivel que corresponda a la oferta.';
  const destinos = destinosManuales(d.acciones_permitidas);
  if (col.destino && destinos.includes(col.destino)) return null;
  if (d.caso.en_proceso) return 'Un agente está trabajando en el caso; espere a que termine para moverlo.';
  if (!destinos.length) return `Desde ${desde} no hay movimientos manuales.`;
  return `No se puede pasar de ${desde} a ${col.titulo}. Desde ${desde} puede ir a: ${destinos.map((e) => ETIQUETA_ESTADO[e as EstadoCaso] ?? e).join(', ')}.`;
}

interface Arrastre {
  caso: CasoResumen;
  col: string;
  carril: string;
}
interface Destino {
  col: string;
  carril: string;
  indice: number;
}

export function Tablero() {
  const sesion = useSesion();
  const ui = useUi();
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const todos = useTodosLosCasos();
  const nombres = useNombres(todos.data?.items);
  const cambiar = useCambiarCaso();
  const filtros = useMemo(() => filtrosDesdeUrl(params), [params]);
  const carriles: Carriles = (params.get('carriles') as Carriles) || leer<Carriles>('tablero-carriles', 'ninguno');
  const vistaClave = (params.get('vista') as VistaPipeline) || 'activos';
  const vista = VISTAS.find((v) => v.clave === vistaClave) ?? VISTAS[0];
  const COLUMNAS = vista.columnas;
  const columnaDe = (estado: EstadoCaso) => COLUMNAS.find((c) => c.estados.includes(estado)) ?? COLUMNAS[0];
  const fijarVista = (v: VistaPipeline) => {
    const p = new URLSearchParams(params);
    if (v === 'activos') p.delete('vista');
    else p.set('vista', v);
    setParams(p, { replace: true });
  };
  const [plegados, setPlegados] = useState<string[]>(() => leer<string[]>('tablero-plegadas', []));
  const [carrilesPlegados, setCarrilesPlegados] = useState<string[]>([]);
  const verFin = veFinanciero(sesion.roles);
  const operar = puedeOperar(sesion.roles);

  const fijarFiltros = useCallback((f: FiltrosRapidos) => setParams(filtrosAUrl(f, params), { replace: true }), [params, setParams]);
  const fijarCarriles = (c: Carriles) => {
    guardar('tablero-carriles', c);
    const p = new URLSearchParams(params);
    if (c === 'ninguno') p.delete('carriles');
    else p.set('carriles', c);
    setParams(p, { replace: true });
  };
  const alternarColumna = (clave: string) => {
    const n = plegados.includes(clave) ? plegados.filter((x) => x !== clave) : [...plegados, clave];
    setPlegados(n);
    guardar('tablero-plegadas', n);
  };

  const casos = useMemo(
    () => aplicarFiltros(todos.data?.items ?? [], filtros, sesion.usuario.oid).filter((c) => vista.filtro(c) && vista.columnas.some((k) => k.estados.includes(c.estado))),
    [todos.data, filtros, sesion.usuario.oid, vista],
  );

  const listaCarriles = useMemo(() => {
    if (carriles === 'ninguno') return [{ clave: '_', titulo: '', casos }];
    if (carriles === 'prioridad') {
      return (['alta', 'media', 'baja'] as Prioridad[]).map((p) => ({ clave: p, titulo: `Prioridad ${ETIQUETA_PRIORIDAD[p].toLowerCase()}`, casos: casos.filter((c) => c.prioridad === p), prioridad: p }));
    }
    const ids = [...new Set(casos.map((c) => c.dueno ?? SIN_DUENO))];
    ids.sort((a, b) => {
      if (a === sesion.usuario.oid) return -1;
      if (b === sesion.usuario.oid) return 1;
      if (a === SIN_DUENO) return 1;
      if (b === SIN_DUENO) return -1;
      return (nombres.get(a) ?? a).localeCompare(nombres.get(b) ?? b, 'es');
    });
    return ids.map((oid) => ({ clave: oid, titulo: oid === SIN_DUENO ? 'Sin asignar' : (nombres.get(oid) ?? oid), casos: casos.filter((c) => (c.dueno ?? SIN_DUENO) === oid), dueno: oid }));
  }, [carriles, casos, nombres, sesion.usuario.oid]);

  // ------------------------------------------------------------ arrastrar y soltar
  const [arrastre, setArrastre] = useState<Arrastre | null>(null);
  const [destino, setDestino] = useState<Destino | null>(null);
  const [detalleArrastre, setDetalleArrastre] = useState<CasoDetalle | null>(null);
  const arrastreRef = useRef<Arrastre | null>(null);

  const cargarDetalle = useCallback(
    (id: string) => qc.fetchQuery({ queryKey: ['caso', id], queryFn: () => api.obtenerCaso(id), staleTime: 15_000 }),
    [qc],
  );

  const alIniciar = (e: DragEvent, caso: CasoResumen, carril: string) => {
    const a = { caso, col: columnaDe(caso.estado).clave, carril };
    arrastreRef.current = a;
    setArrastre(a);
    setDetalleArrastre(null);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', caso.case_id);
    cargarDetalle(caso.case_id)
      .then((d) => arrastreRef.current?.caso.case_id === caso.case_id && setDetalleArrastre(d))
      .catch(() => undefined);
  };

  const alTerminar = () => {
    arrastreRef.current = null;
    setArrastre(null);
    setDestino(null);
    setDetalleArrastre(null);
  };

  const alSobrevolar = (e: DragEvent<HTMLDivElement>, col: string, carril: string) => {
    if (!arrastreRef.current) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    const tarjetas = [...e.currentTarget.querySelectorAll<HTMLElement>('[data-tarjeta]')].filter((t) => t.dataset.tarjeta !== arrastreRef.current?.caso.case_id);
    let indice = tarjetas.length;
    for (let i = 0; i < tarjetas.length; i++) {
      const r = tarjetas[i].getBoundingClientRect();
      if (e.clientY < r.top + r.height / 2) {
        indice = i;
        break;
      }
    }
    if (!destino || destino.col !== col || destino.carril !== carril || destino.indice !== indice) setDestino({ col, carril, indice });
  };

  const alSoltar = async (e: DragEvent, col: Columna, carril: { clave: string; dueno?: string; prioridad?: Prioridad }, celda: CasoResumen[]) => {
    e.preventDefault();
    const a = arrastreRef.current;
    const indice = destino?.indice ?? celda.length;
    alTerminar();
    if (!a) return;
    const c = a.caso;
    const mismaColumna = col.estados.includes(c.estado);
    const mismoCarril = a.carril === carril.clave;
    const vecinos = celda.filter((x) => x.case_id !== c.case_id);
    if (mismaColumna && mismoCarril) {
      const posActual = celda.findIndex((x) => x.case_id === c.case_id);
      if (posActual === indice) return;
    }
    let d: CasoDetalle;
    try {
      d = await cargarDetalle(c.case_id);
    } catch {
      ui.avisar({ tono: 'error', texto: 'No se pudo validar el movimiento', detalle: 'Recargue el tablero e intente de nuevo.' });
      return;
    }
    if (!tieneAccion(d.acciones_permitidas, ACCION.editar)) {
      ui.avisar({ tono: 'alerta', texto: `${c.case_id} no se puede mover`, detalle: razonBloqueo(d, col) ?? 'Su rol no puede modificar este caso.' });
      return;
    }
    if (!mismaColumna) {
      const razon = razonBloqueo(d, col);
      if (razon) {
        ui.avisar({ tono: 'alerta', texto: `Movimiento no permitido para ${c.case_id}`, detalle: razon, accion: { texto: 'Abrir el caso', alHacer: () => ui.abrirCaso(c.case_id) } });
        return;
      }
    }
    // Nuevo orden: punto medio entre los vecinos de la posición de destino.
    const antes = vecinos[indice - 1];
    const despues = vecinos[indice];
    const oA = antes ? ordenEfectivo(antes, indice - 1) : null;
    const oD = despues ? ordenEfectivo(despues, indice) : null;
    const orden = oA !== null && oD !== null ? (oA + oD) / 2 : oA !== null ? oA + 1000 : oD !== null ? oD - 1000 : Math.round(Date.now() / 1000);
    const cambio: Parameters<typeof cambiar.mutate>[0]['cambio'] = { orden_tablero: Math.round(orden * 1000) / 1000 };
    const visible: Partial<CasoResumen> = {};
    if (!mismaColumna && col.destino) cambio.estado = col.destino;
    if (!mismoCarril && carril.dueno !== undefined) {
      cambio.dueno = carril.dueno === SIN_DUENO ? null : carril.dueno;
      visible.dueno_nombre = carril.dueno === SIN_DUENO ? null : (nombres.get(carril.dueno) ?? null);
    }
    if (!mismoCarril && carril.prioridad) cambio.prioridad = carril.prioridad;
    cambiar.mutate({
      caso: { case_id: c.case_id, version: d.caso.version },
      cambio,
      visible: { ...visible, ...(cambio.estado ? { dias_en_estado: 0 } : {}) },
      exito: cambio.estado ? `${c.case_id} pasó a ${ETIQUETA_ESTADO[cambio.estado]}` : undefined,
    });
  };

  const estadoColumna = (col: Columna): 'origen' | 'permitida' | 'bloqueada' | 'pendiente' | null => {
    if (!arrastre) return null;
    if (col.estados.includes(arrastre.caso.estado)) return 'origen';
    if (!detalleArrastre) return 'pendiente';
    return razonBloqueo(detalleArrastre, col) ? 'bloqueada' : 'permitida';
  };

  if (todos.isError) {
    return (
      <div className="pagina">
        <CabeceraTablero carriles={carriles} alCarriles={fijarCarriles} vista={vistaClave} alVista={fijarVista} />
        <PanelError error={todos.error} alReintentar={() => todos.refetch()} />
      </div>
    );
  }

  const columnasConConteo = COLUMNAS.map((col) => ({ col, items: casos.filter((c) => col.estados.includes(c.estado)) }));

  return (
    <div className="pagina pagina--tablero">
      <CabeceraTablero carriles={carriles} alCarriles={fijarCarriles} total={casos.length} vista={vistaClave} alVista={fijarVista} conteos={Object.fromEntries(VISTAS.map((v) => [v.clave, (todos.data?.items ?? []).filter((c) => v.filtro(c) && v.columnas.some((k) => k.estados.includes(c.estado))).length]))} />
      <BarraFiltros casos={todos.data?.items ?? []} filtros={filtros} alCambiar={fijarFiltros} nombres={nombres} />
      {arrastre && (
        <p className="sr" role="status" aria-live="assertive">
          Moviendo {arrastre.caso.case_id}. Suelte sobre una columna resaltada.
        </p>
      )}
      <div
        className={`tablero ${arrastre ? 'tablero--arrastrando' : ''}`}
        role="region"
        aria-label="Tablero de casos"
        tabIndex={0}
        onDragOver={(e) => {
          // Desplazamiento automático cerca de los bordes mientras se arrastra.
          if (!arrastreRef.current) return;
          const r = e.currentTarget.getBoundingClientRect();
          const borde = 72;
          if (e.clientX > r.right - borde) e.currentTarget.scrollLeft += 18;
          else if (e.clientX < r.left + borde) e.currentTarget.scrollLeft -= 18;
          if (e.clientY > r.bottom - borde) e.currentTarget.scrollTop += 14;
          else if (e.clientY < r.top + borde + 40) e.currentTarget.scrollTop -= 14;
        }}
      >
        <div
          className={`tablero__rejilla ${carriles === 'ninguno' ? 'tablero__rejilla--sin-carriles' : ''}`}
          style={{ gridTemplateColumns: COLUMNAS.map((c) => (plegados.includes(c.clave) ? '44px' : 'var(--ancho-columna)')).join(' ') }}
        >
          {columnasConConteo.map(({ col, items }) => {
            const plegada = plegados.includes(col.clave);
            const valor = items.reduce((s, c) => s + (c.moneda === 'COP' ? (c.valor_estimado ?? 0) : 0), 0);
            const est = estadoColumna(col);
            return (
              <div key={col.clave} className={`columna-cabecera ${plegada ? 'columna-cabecera--plegada' : ''} ${est ? `columna-cabecera--${est}` : ''}`} style={{ ['--acento' as string]: COLOR_GRUPO[col.estados.length > 1 || ESTADOS_CERRADOS.includes(col.estados[0]) ? (col.estados[0] === 'GANADO' ? 'cerrados' : col.estados[0] === 'PERDIDO' || col.estados[0] === 'DESISTIDO' ? 'excepcion' : 'entrada') : grupoDeEstado(col.estados[0]).clave] }}>
                <div className="columna-cabecera__fila">
                  <span className="columna-cabecera__titulo">{col.titulo}</span>
                  <button type="button" className="columna-cabecera__boton" onClick={() => alternarColumna(col.clave)} aria-expanded={!plegada} aria-label={`${plegada ? 'Desplegar' : 'Plegar'} columna ${col.titulo}`} data-ayuda={plegada ? col.titulo : 'Plegar columna'} data-ayuda-pos="abajo">
                    <Icono nombre={plegada ? 'chevronDerecha' : 'chevronIzquierda'} />
                  </button>
                </div>
                {!plegada && (
                  <div className="columna-cabecera__fila columna-cabecera__fila--datos">
                    <span className="columna-cabecera__valor">{dineroCorto(valor)}</span>
                    <span className="columna-cabecera__cuenta">
                      {items.length} {items.length === 1 ? 'oportunidad' : 'oportunidades'}
                    </span>
                  </div>
                )}
                {plegada && <span className="columna-cabecera__vertical">{items.length}</span>}
              </div>
            );
          })}

          {todos.isPending
            ? COLUMNAS.map((col, i) => (
                <div key={col.clave} className="celda">
                  {Array.from({ length: 3 - (i % 3) }).map((_, j) => (
                    <div key={j} className="tarjeta-caso tarjeta-caso--esqueleto">
                      <Esqueleto alto={12} ancho="90%" radio={3} />
                      <Esqueleto alto={12} ancho="60%" radio={3} />
                      <Esqueleto alto={10} ancho="40%" radio={3} />
                    </div>
                  ))}
                </div>
              ))
            : listaCarriles.map((carril) => {
                const plegadoCarril = carrilesPlegados.includes(carril.clave);
                return [
                  carriles !== 'ninguno' && (
                    <div key={`h-${carril.clave}`} className="carril">
                      <button
                        type="button"
                        className="carril__boton"
                        aria-expanded={!plegadoCarril}
                        onClick={() => setCarrilesPlegados((l) => (l.includes(carril.clave) ? l.filter((x) => x !== carril.clave) : [...l, carril.clave]))}
                      >
                        <Icono nombre={plegadoCarril ? 'chevronDerecha' : 'chevronAbajo'} />
                        {'dueno' in carril && <Avatar oid={carril.dueno === SIN_DUENO ? null : carril.dueno} nombre={carril.dueno === SIN_DUENO ? null : carril.titulo} tam={24} titulo={false} />}
                        {'prioridad' in carril && carril.prioridad && <IconoPrioridad prioridad={carril.prioridad} />}
                        <strong>{carril.titulo}</strong>
                        <span className="carril__cuenta">{carril.casos.length} {carril.casos.length === 1 ? 'caso' : 'casos'}</span>
                      </button>
                    </div>
                  ),
                  ...(plegadoCarril
                    ? []
                    : COLUMNAS.map((col) => {
                        const celda = carril.casos.filter((c) => col.estados.includes(c.estado)).sort(ordenar);
                        const plegada = plegados.includes(col.clave);
                        const est = estadoColumna(col);
                        const esDestino = destino && destino.col === col.clave && destino.carril === carril.clave;
                        return (
                          <div
                            key={`${carril.clave}-${col.clave}`}
                            className={`celda ${plegada ? 'celda--plegada' : ''} ${est ? `celda--${est}` : ''} ${esDestino ? 'celda--sobre' : ''}`}
                            onDragOver={(e) => alSobrevolar(e, col.clave, carril.clave)}
                            onDragLeave={(e) => {
                              if (!e.currentTarget.contains(e.relatedTarget as Node)) setDestino((d) => (d && d.col === col.clave && d.carril === carril.clave ? null : d));
                            }}
                            onDrop={(e) => void alSoltar(e, col, carril as { clave: string; dueno?: string; prioridad?: Prioridad }, celda)}
                            aria-label={`${col.titulo}${carril.titulo ? `, ${carril.titulo}` : ''}`}
                            role="list"
                          >
                            {plegada ? (
                              <span className="celda__vertical">{celda.length > 0 ? `${celda.length}` : ''}</span>
                            ) : (
                              <>
                                {celda.map((c, i) => {
                                  const visibleIdx = celda.filter((x) => x.case_id !== arrastre?.caso.case_id).indexOf(c);
                                  return (
                                    <div key={c.case_id} role="listitem" className="celda__item">
                                      {esDestino && destino.indice === visibleIdx && arrastre?.caso.case_id !== c.case_id && <div className="indicador-soltar" aria-hidden="true" />}
                                      <TarjetaCaso
                                        caso={c}
                                        verFin={verFin}
                                        compuesta={col.estados.length > 1}
                                        arrastrable={operar}
                                        arrastrando={arrastre?.caso.case_id === c.case_id}
                                        alIniciar={(e) => alIniciar(e, c, carril.clave)}
                                        alTerminar={alTerminar}
                                        nombreDueno={c.dueno ? (c.dueno_nombre ?? nombres.get(c.dueno) ?? c.dueno) : null}
                                        posicion={i}
                                      />
                                    </div>
                                  );
                                })}
                                {esDestino && destino.indice >= celda.filter((x) => x.case_id !== arrastre?.caso.case_id).length && <div className="indicador-soltar" aria-hidden="true" />}
                                {celda.length === 0 && !arrastre && carriles === 'ninguno' && <p className="celda__vacia">Sin casos</p>}
                                {est === 'bloqueada' && <p className="celda__bloqueo">No disponible</p>}
                              </>
                            )}
                          </div>
                        );
                      })),
                ];
              })}
        </div>
      </div>
      {!todos.isPending && casos.length === 0 && (
        <p className="tablero__nota">
          Ningún caso coincide con los filtros. <button type="button" className="btn btn--enlace" onClick={() => fijarFiltros({ q: '', duenos: [], tipos: [], prioridades: [], etiquetas: [], mios: false, vencidos: false })}>Limpiar filtros</button>
        </p>
      )}
    </div>
  );
}

function CabeceraTablero({
  carriles,
  alCarriles,
  total,
  vista,
  alVista,
  conteos,
}: {
  carriles: Carriles;
  alCarriles: (c: Carriles) => void;
  total?: number;
  vista: VistaPipeline;
  alVista: (v: VistaPipeline) => void;
  conteos?: Record<string, number>;
}) {
  const ui = useUi();
  const sesion = useSesion();
  const opciones: { v: Carriles; t: string }[] = [
    { v: 'ninguno', t: 'Sin carriles' },
    { v: 'dueno', t: 'Por responsable' },
    { v: 'prioridad', t: 'Por prioridad' },
  ];
  return (
    <>
      <header className="pagina__cabecera">
        <div className="pagina__titulos">
          <h1 className="pagina__titulo">Pipeline de preventa</h1>
          {esPreventa(sesion.roles) && (
            <button type="button" className="boton-redondo" onClick={ui.abrirCrear} aria-label="Crear caso" data-ayuda="Crear caso" data-ayuda-pos="abajo">
              <Icono nombre="mas" />
            </button>
          )}
          {total !== undefined && <span className="pagina__conteo">{total} oportunidades</span>}
        </div>
        <div className="pagina__acciones">
          <Popover
            ubicacion="abajo-fin"
            ancho={220}
            rol="menu"
            etiqueta="Carriles"
            disparador={({ ref, alternar, props }) => (
              <button ref={ref} type="button" className="btn btn--secundario btn--pequeno" onClick={alternar} {...props}>
                <Icono nombre="carriles" />
                {opciones.find((o) => o.v === carriles)?.t}
                <Icono nombre="chevronAbajo" />
              </button>
            )}
          >
            {(cerrar) => (
              <div className="menu" role="menu" aria-label="Carriles">
                <SeparadorMenu titulo="Agrupar el tablero" />
                {opciones.map((o) => (
                  <ItemMenu
                    key={o.v}
                    rol="menuitemradio"
                    seleccionado={carriles === o.v}
                    onClick={() => {
                      alCarriles(o.v);
                      cerrar();
                    }}
                  >
                    {o.t}
                  </ItemMenu>
                ))}
              </div>
            )}
          </Popover>
        </div>
      </header>
      <Pestanas
        etiqueta="Vistas del pipeline"
        activa={vista}
        alCambiar={(k) => alVista(k as VistaPipeline)}
        pestanas={VISTAS.map((v) => ({ clave: v.clave, titulo: v.titulo, contador: conteos?.[v.clave] }))}
      />
    </>
  );
}

function MenuTarjeta({ caso }: { caso: CasoResumen }) {
  const ui = useUi();
  const cambiar = useCambiarCaso();
  const [abierto, setAbierto] = useState(false);
  const d = useQuery({ queryKey: ['caso', caso.case_id], queryFn: () => api.obtenerCaso(caso.case_id), enabled: abierto, staleTime: 15_000 });
  const destinos = destinosManuales(d.data?.acciones_permitidas) as EstadoCaso[];
  return (
    <Popover
      ubicacion="abajo-fin"
      ancho={240}
      rol="menu"
      etiqueta={`Acciones de ${caso.case_id}`}
      abierto={abierto}
      alCambiar={setAbierto}
      disparador={({ ref, alternar, props }) => (
        <button
          ref={ref}
          type="button"
          className="tarjeta-caso__menu btn-icono btn-icono--mini"
          aria-label={`Acciones de ${caso.case_id}`}
          onClick={(e) => {
            e.stopPropagation();
            alternar();
          }}
          {...props}
        >
          <Icono nombre="masH" />
        </button>
      )}
    >
      {(cerrar) => (
        <div className="menu" role="menu" aria-label={`Acciones de ${caso.case_id}`}>
          <ItemMenu
            icono={<Icono nombre="pantallaCompleta" />}
            onClick={() => {
              cerrar();
              ui.abrirCaso(caso.case_id);
            }}
          >
            Abrir espacio de trabajo
          </ItemMenu>
          <ItemMenu
            icono={<Icono nombre="colapsar" />}
            onClick={() => {
              cerrar();
              ui.abrirVistaRapida(caso.case_id);
            }}
          >
            Vista rápida
          </ItemMenu>
          <SeparadorMenu titulo="Mover a" />
          {d.isPending && <p className="menu__vacio">Consultando movimientos permitidos…</p>}
          {d.isSuccess && destinos.length === 0 && <p className="menu__vacio">Sin movimientos manuales disponibles.</p>}
          {destinos.map((e) => (
            <ItemMenu
              key={e}
              icono={<span className={`punto-estado punto-estado--${claseEstado(e)}`} />}
              onClick={() => {
                cerrar();
                cambiar.mutate({ caso: { case_id: caso.case_id, version: d.data?.caso.version }, cambio: { estado: e }, visible: { dias_en_estado: 0 }, exito: `${caso.case_id} pasó a ${ETIQUETA_ESTADO[e]}` });
              }}
            >
              {ETIQUETA_ESTADO[e] ?? e}
            </ItemMenu>
          ))}
        </div>
      )}
    </Popover>
  );
}

function TarjetaCaso({
  caso: c,
  verFin,
  compuesta,
  arrastrable,
  arrastrando,
  alIniciar,
  alTerminar,
  nombreDueno,
}: {
  caso: CasoResumen;
  verFin: boolean;
  compuesta: boolean;
  arrastrable: boolean;
  arrastrando: boolean;
  alIniciar: (e: DragEvent) => void;
  alTerminar: () => void;
  nombreDueno: string | null;
  posicion: number;
}) {
  const ui = useUi();
  const tarde = estaVencido(c);
  const cerrado = ESTADOS_CERRADOS.includes(c.estado);
  const salud = c.puntaje_salud ?? null;
  return (
    <article
      className={`tarjeta-caso ${arrastrando ? 'tarjeta-caso--arrastrando' : ''} ${tarde ? 'tarjeta-caso--vencida' : ''}`}
      data-tarjeta={c.case_id}
      draggable={arrastrable}
      onDragStart={alIniciar}
      onDragEnd={alTerminar}
      onClick={(e) => {
        if ((e.target as HTMLElement).closest('a, button')) return;
        ui.abrirCaso(c.case_id);
      }}
    >
      <a
        href={hrefRuta(`/preventa/casos/${c.case_id}`)}
        className="tarjeta-caso__enlace"
        draggable={false}
        onClick={(e) => {
          if (e.ctrlKey || e.metaKey || e.shiftKey) return;
          e.preventDefault();
          ui.abrirCaso(c.case_id);
        }}
      >
        <span className="tarjeta-caso__titulo">
          <span className="tarjeta-caso__cliente-t">{c.cliente}</span>
          <span className="tarjeta-caso__sep"> · </span>
          <span className={`tarjeta-caso__clave ${cerrado ? 'tarjeta-caso__clave--cerrada' : ''}`}>{c.case_id}</span>
        </span>
        <span className="tarjeta-caso__tema">{c.tema}</span>
      </a>
      <MenuTarjeta caso={c} />
      <dl className="tarjeta-caso__datos">
        <div>
          <dt>Valor</dt>
          <dd className="num-tab" title={dinero(c.valor_estimado, c.moneda)}>
            {c.valor_estimado != null ? dinero(c.valor_estimado, c.moneda) : 'Sin valor'}
          </dd>
        </div>
        <div>
          <dt>Estado</dt>
          <dd className="tarjeta-caso__estado">
            {verFin && c.semaforo_rentabilidad && <span className={`cuadro-estado cuadro-estado--${c.semaforo_rentabilidad.toLowerCase()}`} title={`Rentabilidad ${c.rentabilidad != null ? pct(c.rentabilidad) : ''} (${c.semaforo_rentabilidad.toLowerCase()})`} />}
            {verFin && c.semaforo_riesgo && <span className={`cuadro-estado cuadro-estado--${c.semaforo_riesgo.toLowerCase()}`} title={`Riesgo ${c.semaforo_riesgo.toLowerCase()}`} />}
            <span className={`cuadro-estado cuadro-estado--${tarde ? 'rojo' : 'verde'}`} title={tarde ? 'Fecha límite vencida' : 'Fecha límite al día'} />
            {compuesta ? <ChipEstado estado={c.estado} /> : <IconoPrioridad prioridad={c.prioridad} />}
            {verFin && c.rentabilidad != null && <span className="texto-sec pequeno num-tab">{pct(c.rentabilidad)}</span>}
          </dd>
        </div>
        <div>
          <dt>Responsable</dt>
          <dd className="tarjeta-caso__persona">
            <Avatar oid={c.dueno} nombre={nombreDueno} tam={20} titulo={false} />
            <span>{nombreDueno ?? 'Sin asignar'}</span>
          </dd>
        </div>
        <div>
          <dt>Fecha límite</dt>
          <dd className={tarde ? 'texto-peligro fuerte' : ''}>
            {c.fecha_limite ? fecha(c.fecha_limite) : 'Sin fecha'}
            {tarde && <span className="sr"> (vencida)</span>}
          </dd>
        </div>
      </dl>
      <div className="tarjeta-caso__pie">
        <PildoraSalud puntaje={salud} />
        {!!c.comentarios && (
          <span className="tarjeta-caso__meta" title={`${c.comentarios} comentarios`}>
            <Icono nombre="comentario" />
            {c.comentarios}
          </span>
        )}
        <IconoTipo tipo={c.tipo} />
      </div>
    </article>
  );
}
